import React, { ReactElement, useState } from 'react';
import {
  SearchOutlined,
} from '@ant-design/icons';
import lodashMerge from 'lodash.merge';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { App } from 'common/components/antd/App';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { Select } from 'common/components/antd/Select/Select';
import { Table } from 'common/components/antd/Table/Table';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_ADMIN_FE } from 'common/constants/general-access-role.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { TCounterpartiesListResData } from 'domains/counterparties/types/counterparty.types';
import { useGetCounterpartyCustomersMutation } from 'domains/customers/queries/customers.query';
import { TCustomersEntity } from 'domains/customers/types/customers.types';
import { ELimitsPeriod } from 'domains/limits/enums/limits.enum';
import {
  useGetLimitsCustomerAmountMutation,
  useGetLimitsCustomerPaymentMutation,
} from 'domains/limits/queries/limits.query';
import {
  TGetLimitsCustomerAmountDataResponse,
  TGetLimitsCustomerAmountItem,
  TGetLimitsCustomerPaymentDataResponse,
  TGetLimitsCustomerPaymentItem,
} from 'domains/limits/types/limits.types';
import { useGetSegmentsMutation } from 'domains/segments/queries/segments.query';
import { TSegmentsResponse } from 'domains/segments/types/segments.types';
import { ReactComponent as NoResultIcon } from 'assets/ibm/no-result.svg';
import { ELimitsSearchForm, ETableRowType } from './enums/individual-limits.enums';
import {
  NoDataContainer,
  SFormInnerWrapper,
  SFormInputsContainer,
  SHeaderForm,
  SNoResultText,
} from './individual-limits.page.styles';
import SetLimitsModal from './modals/set-limits.modal';
import getTableColumns from './table/table.columns';
import { ETableColumns } from './table/table.enum';
import { TLimitsTableRecord } from './table/table.types';
import { TIndividualLimitsModalData, TIndividualLimitsPageLoaderData } from './types/individual-limits.types';

const LimitsIndividualLimitsPage = (): ReactElement => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [isSearchBtnLoading, setIsSearchBtnLoading] = useState<boolean>(false);
  const [modalData, setModalData] = useState<TIndividualLimitsModalData | {}>({});
  const [tableData, setTableData] = useState<TLimitsTableRecord[] | []>([]);
  const [activeCounterpartyData, setActiveCounterpartyData] = useState<{
    counterpartyLabel: string,
    counterpartyGUID: string,
  }>({
    counterpartyLabel: '',
    counterpartyGUID: '',
  });
  const [activeAccountId, setActiveAccountId] = useState<string>('');
  const { useApp } = App;
  const { notification } = useApp();
  const {
    counterpartiesData,
  } = useLoaderData() as TIndividualLimitsPageLoaderData;

  const {
    mutateAsync: mutateAsyncGetSegmentsMutation,
  } = useGetSegmentsMutation();

  const {
    mutateAsync: mutateAsyncGetCounterpartyCustomersMutation,
  } = useGetCounterpartyCustomersMutation();

  const {
    mutateAsync: mutateAsyncGetLimitsCustomerAmountMutation,
  } = useGetLimitsCustomerAmountMutation();

  const {
    mutateAsync: mutateAsyncGetLimitsCustomerPaymentMutation,
  } = useGetLimitsCustomerPaymentMutation();

  const onCellClickHandler = (record: TLimitsTableRecord, cellKey: string[]): void => {
    const periodKey = [...cellKey].pop() as string;
    const periodLabel = periodKey === ELimitsPeriod.Days
      ? t('limits.individualLimits.modal.edit.fields.label.period.days')
      : periodKey.charAt(0).toUpperCase() + periodKey.slice(1);
    const amount = cellKey?.reduce((acc: any, item: string) => acc[item], record);
    const maxAmount = amount?.max;
    const minAmount = amount?.min;
    const amountPeriodKey = amount?.period_key;

    const collectModalData: TIndividualLimitsModalData = {
      type: record.type,
      accountTypeGuid: record.account_type_guid,
      counterpartyLabel: activeCounterpartyData.counterpartyLabel,
      counterpartyGUID: activeCounterpartyData.counterpartyGUID,
      segmentLabel: record.segment_name,
      typeLabel: record.type,
      periodLabel,
      periodKey,
      maxAmount,
      minAmount: minAmount || 0,
      amountPeriodKey,
      assetCode: record.asset_code,
      assetIssuer: record.asset_issuer,
      customerGUID: record.customer_guid,
      userName: record.user_name,
    };

    setModalData(collectModalData);
  };

  const columns = getTableColumns(onCellClickHandler);

  const generateLimitsTableData = <
    Data extends TGetLimitsCustomerAmountDataResponse | TGetLimitsCustomerPaymentDataResponse,
    DataItem extends TGetLimitsCustomerAmountItem | TGetLimitsCustomerPaymentItem
  >(
    responseData: Data,
    counterpartyGUID: string,
    customerGUID: string,
    type: ETableRowType,
    customerData: TCustomersEntity,
    segmentsData: TSegmentsResponse,
  ): TLimitsTableRecord[] => {
    const currentLimitsData = segmentsData.filter(
      currentLimitData => currentLimitData.counterparty.guid === counterpartyGUID
    )[0];
    const assetsList = currentLimitsData.segments[customerData.segment as string]
    const formattedLimitsUsersData = (responseData?.columns || new Array())
      .reduce((limitsAcc: { [key: string]: DataItem}, limitData: DataItem) => {
        let generatedLimitData = limitData;

        if (type === ETableRowType.Operation) {
          generatedLimitData = {
            period: {
              day: {
                min: (limitData as TGetLimitsCustomerPaymentItem).min,
                max: (limitData as TGetLimitsCustomerPaymentItem).max,
              }
            },
            ...limitData,
          }
        }

        return {
          ...limitsAcc,
          [limitData?.account_type_guid]: generatedLimitData,
        };
      }, {});
    const dataTableSkeleton = assetsList.map(({
      account_type_guid,
      asset_code,
      asset_issuer ,
    }) => {
      const dataAssetRowFromApi = formattedLimitsUsersData[account_type_guid];
      const dataAssetRowSkeleton = {
        type: type === ETableRowType.Operation
          ? t('limits.individualLimits.table.period.column.type.value.operation')
          : t('limits.individualLimits.table.period.column.type.value.user'),
        segment_name: customerData.segment,
        user_name: customerData.login,
        account_type_guid,
        asset_code,
        asset_issuer,
        customer_guid: customerGUID,
        ...(type === ETableRowType.Operation ? {
          period: {
            day: { // dirty hack for colSpan
              min: null,
              max: null,
            },
          },
        } : null),
        ...(type === ETableRowType.User ? {
          period: {
            day: {
              period_key: 1,
              max: null,
            },
            days: {
              period_key: 1,
              max: null,
            },
            month: {
              period_key: 1,
              max: null,
            },
            week: {
              period_key: 1,
              max: null,
            },
            year: {
              period_key: 1,
              max: null,
            },
          },
        } : null),
      };

      return lodashMerge(dataAssetRowSkeleton, dataAssetRowFromApi);
    });

    return dataTableSkeleton;
  };

  const updateTableData = async (counterpartyGUID: string, accountID: string): Promise<void> => {
    const reqData = {
      counterpartyGUID,
      params: {
        customer_guid: accountID,
      }
    };

    const segmentsData = await mutateAsyncGetSegmentsMutation({ counterpartyGUID });

    const customersData = await mutateAsyncGetCounterpartyCustomersMutation({
      counterpartyGUID,
      params: {
        wallet_user_guids: [accountID],
      },
    });
    const customerData = customersData.customers?.[0] as TCustomersEntity;

    if (customerData) {
      setActiveCounterpartyData(
        {
          counterpartyLabel: counterpartiesData?.counterparties
            .filter(({ guid }) => guid === counterpartyGUID)[0].name,
          counterpartyGUID,
        }
      );

      setActiveAccountId(accountID);

      const amountResponseData = await mutateAsyncGetLimitsCustomerAmountMutation(reqData);
      const amountTableData = generateLimitsTableData<
        TGetLimitsCustomerAmountDataResponse,
        TGetLimitsCustomerAmountItem
      >(amountResponseData, counterpartyGUID, accountID, ETableRowType.User, customerData, segmentsData);
      const paymentResponseData = await mutateAsyncGetLimitsCustomerPaymentMutation(reqData);
      const paymentTableData = generateLimitsTableData<
        TGetLimitsCustomerPaymentDataResponse,
        TGetLimitsCustomerPaymentItem
      >(paymentResponseData, counterpartyGUID, accountID, ETableRowType.Operation, customerData, segmentsData);

      setTableData([...paymentTableData, ...amountTableData]);
    } else {
      notification.warning({
        message: t('limits.individualLimits.notification.warning'),
        type: 'warning',
      });
    }
  };

  const onFinishFormHandler = async (values: Record<ELimitsSearchForm, string>): Promise<void> => {
    try {
      setIsSearchBtnLoading(true);

      const {
        accId,
        counterparty
      } = values;

      await updateTableData(counterparty || ENV_COUNTERPARTY_GUID, accId);

      setIsSearchBtnLoading(false);
    } catch (error) {
      console.error(error);
      setIsSearchBtnLoading(false);
    }
  };

  const onCancelModalHandler = (): void => {
    setModalData({});
  };

  const invalidateDataTableHandler = async (): Promise<void> => {
    try {
      const {
        counterpartyGUID,
      } = activeCounterpartyData;

      await updateTableData(counterpartyGUID, activeAccountId);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <SHeaderForm>
        <Form form={form} onFinish={onFinishFormHandler}>
          <SFormInnerWrapper>
            <SFormInputsContainer>
              {IS_ADMIN_FE && (
                <Form.Item name={ELimitsSearchForm.Counterparty}>
                  <Select
                    placeholder={t('limits.individualLimits.head.input.placeholder.counterparties') as string}
                  >
                    {counterpartiesData?.counterparties.map(
                      ({ guid, name }) => (
                        <Select.Option key={guid} value={guid}>
                          {name}
                        </Select.Option>
                      )
                    )}
                  </Select>
                </Form.Item>
              )}
              <Form.Item name={ELimitsSearchForm.AccId}>
                <Input
                  placeholder={t('limits.individualLimits.head.input.placeholder.accNumber') as string}
                  suffix={<SearchOutlined />}
                />
              </Form.Item>
            </SFormInputsContainer>
            <Button
              loading={isSearchBtnLoading}
              htmlType="submit"
              type="primary"
            >
              {t('limits.individualLimits.head.button.search')}
            </Button>
          </SFormInnerWrapper>
        </Form>
      </SHeaderForm>
      {tableData.length ? (
        <Table
          columns={columns as any}
          dataSource={tableData}
          rowKey={ETableColumns.AsseCode}
        />
      ) : (
        <NoDataContainer>
          <NoResultIcon />
          <SNoResultText>
            {t('limits.individualLimits.noResult')}
          </SNoResultText>
        </NoDataContainer>
      )}
      <SetLimitsModal
        isOpen={!!Object.keys(modalData).length}
        onCancelModalHandler={onCancelModalHandler}
        data={modalData as TIndividualLimitsModalData}
        invalidateDataTableHandler={invalidateDataTableHandler}
      />
    </>
  );
};

export const loader = async (): Promise<{ counterpartiesData: TCounterpartiesListResData | {} }> => {
  try {
    const counterpartiesListQuery = queryKeysClient[EQueryKeys.CounterpartyQueryKeys].getCounterpartiesList();

    const counterpartiesData: TCounterpartiesListResData =
      queryClient.getQueryData(counterpartiesListQuery.queryKey) ??
      (await queryClient.fetchQuery(counterpartiesListQuery));

    return {
      counterpartiesData,
    };
  } catch (error) {
    console.error(error);

    return {
      counterpartiesData: {},
    }
  }
};

export default LimitsIndividualLimitsPage;
